import { polygonArea, toVec3 } from '../geo/sphere.js';
import { add, normalise } from '../geo/vec3.js';
import { Gnomonic, bounds2, centroid2, clipByHalfPlanes, contains2, powerBisector, } from './gnomonic.js';
import { hashSeed, makeRandom } from '../art/coastline.js';
/**
 * How wrong a cell's area is.
 *
 * Relative error alone is the wrong measure: holding a territory that should
 * occupy 1% of a continent to within 5% of itself is a far harsher demand than
 * holding one that should occupy half, and the difference is invisible on the
 * globe either way. The floor makes the measure relative for territories that
 * matter and absolute for slivers.
 */
export function areaError(got, wanted, parentArea) {
    const floor = 0.02 * parentArea;
    return Math.abs(got - wanted) / Math.max(wanted + floor, 1e-12);
}
/**
 * Deterministic starting points, spread inside the parent polygon itself.
 *
 * Seeding outside the land is the difference between nine territories and four
 * territories and five starved slivers that never recover, so candidates are
 * rejection-sampled against the polygon rather than scattered over its bounding
 * cap.
 */
function seedSites(parent2, count, seed) {
    const box = bounds2(parent2);
    const random = makeRandom(seed);
    const found = [];
    // A jittered low-discrepancy sweep gives an even spread without clumping.
    const golden = 0.618033988749895;
    let t = random();
    for (let attempt = 0; attempt < count * 400 && found.length < count; attempt += 1) {
        t = (t + golden) % 1;
        const u = (t + random() * 0.15) % 1;
        const v = ((attempt * golden * golden) % 1) * 0.85 + random() * 0.15;
        const candidate = {
            x: box.minX + u * (box.maxX - box.minX),
            y: box.minY + v * (box.maxY - box.minY),
        };
        if (!contains2(parent2, candidate))
            continue;
        // Keep them apart, so no two cells start on top of each other.
        const spacing = Math.min(box.maxX - box.minX, box.maxY - box.minY) / (2 * Math.sqrt(count));
        if (found.some((p) => Math.hypot(p.x - candidate.x, p.y - candidate.y) < spacing))
            continue;
        found.push(candidate);
    }
    // Whatever is still missing goes near the middle, jittered, and the
    // relaxation sorts it out from there.
    const middle = centroid2(parent2);
    while (found.length < count) {
        found.push({
            x: middle.x + (random() - 0.5) * (box.maxX - box.minX) * 0.1,
            y: middle.y + (random() - 0.5) * (box.maxY - box.minY) * 0.1,
        });
    }
    return found;
}
/**
 * MAP-094. Partition a parent polygon into territories whose areas match their
 * importance shares.
 *
 * Weights on a power diagram control how much each site captures, so this is a
 * root-find: nudge each weight by the log of how far its area is from target,
 * recompute, repeat. Sites also drift toward their cell's centroid, which keeps
 * the shapes compact rather than slivered.
 */
/**
 * Runs the relaxation, and if it does not settle, tries again from a different
 * deterministic seed and keeps the better result. A handful of starting
 * arrangements are simply bad ones; retrying is far cheaper and far more
 * reliable than trying to tune a schedule that works for every shape.
 */
export function partition(parentRing, targets, options) {
    // A longer single run beats more short ones: restarts are for genuinely bad
    // starting arrangements, not for arrangements that simply need more time.
    const attempts = options.restarts ?? 4;
    let best = null;
    let bestLost = Infinity;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        // The warm start is only worth one go. When the previous solution is
        // itself what is crushing the small cells, starting from it again just
        // crushes them again — so every retry starts fresh.
        const { previous, ...fresh } = options;
        const attemptOptions = attempt === 0 && previous
            ? { ...fresh, previous, seed: options.seed }
            : { ...fresh, seed: options.seed + attempt * 7919 };
        const result = relax(parentRing, targets, attemptOptions);
        // A cell with fewer than three points is not a small territory, it is a
        // missing one: the region it belongs to would silently disappear off the
        // map. That is a failed attempt however well the areas matched.
        const lost = result.cells.filter((cell) => cell.ring.length < 3).length;
        // Everything placed and the areas roughly right is a good result. Spending
        // another full relaxation to chase the last few percent of area accuracy
        // costs seconds of a frozen screen and moves borders nobody can see.
        if (lost === 0 && (result.converged || result.worstError < (options.acceptError ?? 0.18))) {
            return result;
        }
        if (lost < bestLost || (lost === bestLost && (!best || result.worstError < best.worstError))) {
            best = result;
            bestLost = lost;
        }
    }
    return best;
}
function relax(parentRing, targets, options) {
    const totalArea = polygonArea(parentRing);
    const maxIterations = options.maxIterations ?? 800;
    const tolerance = options.tolerance ?? 0.05;
    if (targets.length === 0)
        return { cells: [], iterations: 0, worstError: 0, converged: true };
    const ROUND_LENGTH = 60;
    const centre = normalise(parentRing.reduce((acc, v) => add(acc, v), { x: 0, y: 0, z: 0 }));
    if (targets.length === 1) {
        const only = targets[0];
        return {
            cells: [{ id: only.id, ring: [...parentRing], area: totalArea, site: centre, weight: 0 }],
            iterations: 0,
            worstError: 0,
            converged: true,
        };
    }
    // Everything below happens in the gnomonic plane about the parent's centre,
    // where power-diagram cells are convex polygons that tile exactly. On the
    // sphere the same construction stops being convex once weights approach the
    // site spacing, and clipping silently loses slivers of land.
    const projection = new Gnomonic(centre);
    const parent2 = projection.projectRing(parentRing);
    if (!parent2) {
        // A landmass spanning more than a hemisphere cannot be projected; it is not
        // a shape this product ever authors, but fail honestly rather than silently.
        return { cells: [], iterations: 0, worstError: Number.POSITIVE_INFINITY, converged: false };
    }
    const fresh = seedSites(parent2, targets.length, options.seed);
    const warmStarted = targets.some((t) => options.previous?.has(t.id));
    const sites = targets.map((t, i) => {
        const warm = options.previous?.get(t.id)?.site;
        const projected = warm ? projection.project(warm) : null;
        return projected ?? fresh[i];
    });
    const weights = targets.map((t) => options.previous?.get(t.id)?.weight ?? 0);
    // Where each warm-started territory was, so it can be held near it. Inertia
    // alone is not enough: Lloyd relaxation has its own attractor states, and
    // given enough iterations it forgets where it started entirely.
    const anchors = targets.map((t) => {
        const warm = options.previous?.get(t.id)?.site;
        return warm ? projection.project(warm) : null;
    });
    if (warmStarted) {
        // A newcomer is placed inside whichever existing territory has the most
        // room, rather than wherever the cold-start spread happens to put it. That
        // is what keeps "add a tenth child" from rearranging the other nine.
        const known = targets
            .map((t, i) => ({ i, warm: options.previous?.has(t.id) ?? false }))
            .filter((e) => e.warm)
            .map((e) => e.i);
        const newcomers = targets.map((_, i) => i).filter((i) => !known.includes(i));
        for (const newcomer of newcomers) {
            const cells = known.map((i) => {
                const halves = known
                    .filter((j) => j !== i)
                    .map((j) => powerBisector(sites[i], weights[i], sites[j], weights[j]));
                return { i, ring: clipByHalfPlanes(parent2, halves) };
            });
            let roomiest = cells[0];
            let roomiestArea = -1;
            for (const cell of cells) {
                const area = cell.ring.length >= 3 ? polygonArea(projection.unprojectRing(cell.ring)) : 0;
                if (area > roomiestArea) {
                    roomiestArea = area;
                    roomiest = cell;
                }
            }
            if (roomiest && roomiest.ring.length >= 3) {
                const jitter = makeRandom(hashSeed(options.seed, newcomer));
                const middle = centroid2(roomiest.ring);
                const host = sites[roomiest.i];
                sites[newcomer] = {
                    x: middle.x + (middle.x - host.x) * 0.4 + (jitter() - 0.5) * 1e-3,
                    y: middle.y + (middle.y - host.y) * 0.4 + (jitter() - 0.5) * 1e-3,
                };
                weights[newcomer] = weights[roomiest.i];
                known.push(newcomer);
            }
        }
    }
    let rings2 = [];
    let worstError = Number.POSITIVE_INFINITY;
    let iterations = 0;
    // The relaxation can overshoot and wander, especially when one child is far
    // larger than its siblings. Rather than trusting wherever it happens to stop,
    // keep the best arrangement seen and return that.
    let best = null;
    const cellsFor = () => targets.map((_, i) => {
        const halves = sites
            .map((other, j) => j === i ? null : powerBisector(sites[i], weights[i], other, weights[j]))
            .filter((h) => h !== null);
        return clipByHalfPlanes(parent2, halves);
    });
    for (; iterations < maxIterations; iterations += 1) {
        rings2 = cellsFor();
        // Areas are measured on the sphere, not in the plane: the projection is not
        // area-preserving, and it is the real area on the globe that must match the
        // importance share.
        const areas = rings2.map((r) => (r.length >= 3 ? polygonArea(projection.unprojectRing(r)) : 0));
        worstError = 0;
        for (const [i, target] of targets.entries()) {
            const wanted = target.share * totalArea;
            const got = areas[i];
            worstError = Math.max(worstError, areaError(got, wanted, totalArea));
        }
        if (!best || worstError < best.error) {
            best = {
                rings: rings2.map((r) => [...r]),
                sites: sites.map((p) => ({ ...p })),
                weights: [...weights],
                error: worstError,
            };
        }
        if (worstError <= tolerance)
            break;
        // Grow the starved, shrink the greedy. In Laguerre space a weight has units
        // of length squared, so the step is scaled by the size of a cell — which is
        // the parent's extent divided among the children, not the parent's extent
        // itself. With four children the difference hardly shows; with thirty the
        // step was an order of magnitude too large, the weights thrashed, and the
        // areas stopped responding to them at all. A city asking for a quarter of
        // its country got a fifteenth, and asking for more changed nothing.
        const spread = Math.max(...parent2.map((p) => Math.hypot(p.x, p.y)), 1e-6);
        const extent = spread / Math.sqrt(targets.length);
        const box = bounds2(parent2);
        // Damping decays within a round so the cells settle, and the round resets
        // it: a single monotonically decaying schedule freezes the solver wherever
        // it happens to be, which for evenly weighted children is reliably a few
        // percent short.
        const withinRound = iterations % ROUND_LENGTH;
        const damping = 0.35 / (1 + withinRound * 0.05);
        for (const [i, target] of targets.entries()) {
            const wanted = Math.max(target.share * totalArea, 1e-12);
            const got = Math.max(areas[i], 1e-12);
            const correction = Math.max(-3, Math.min(3, Math.log(wanted / got)));
            weights[i] = weights[i] + damping * extent * extent * correction;
        }
        // Keep the weights centred so they cannot drift off together.
        const mean = weights.reduce((a, b) => a + b, 0) / weights.length;
        for (let i = 0; i < weights.length; i += 1)
            weights[i] = weights[i] - mean;
        // Lloyd step: drift each site toward its cell's centroid so cells stay
        // compact. A cell that has been squeezed out has no centroid to aim at, so
        // it is sent into the largest cell, which is where there is room to recover;
        // aiming it at the middle just piles the starved cells on top of each other.
        let largest = 0;
        for (let i = 1; i < areas.length; i += 1) {
            if (areas[i] > areas[largest])
                largest = i;
        }
        const refuge = (rings2[largest]?.length ?? 0) >= 3 ? centroid2(rings2[largest]) : { x: 0, y: 0 };
        for (const [i, ring] of rings2.entries()) {
            const site = sites[i];
            if (ring.length >= 3) {
                const inertia = warmStarted ? 0.85 : 0.7;
                const towards = centroid2(ring);
                let moved = {
                    x: site.x * inertia + towards.x * (1 - inertia),
                    y: site.y * inertia + towards.y * (1 - inertia),
                };
                // Spring back toward where this territory used to be, so adding a tenth
                // child does not rearrange the other nine. The areas are still governed
                // by the weights, which the spring does not touch.
                const anchor = anchors[i];
                if (anchor) {
                    const pull = 0.25;
                    moved = {
                        x: moved.x * (1 - pull) + anchor.x * pull,
                        y: moved.y * (1 - pull) + anchor.y * pull,
                    };
                }
                sites[i] = moved;
            }
            else {
                // Restart it inside the roomiest cell, on that cell's own terms: the
                // same weight, so it splits its host rather than either vanishing (a
                // stale low weight can never win anything back) or swallowing the
                // continent (a weight above every other makes the cells ping-pong).
                // The offset must be unique per site: two rescued sites landing on the
                // same point produce two identical cells that both claim the same land,
                // which looks like a perfect partition until the areas are added up.
                const jitter = makeRandom(hashSeed(options.seed, i, iterations));
                const spread = Math.max(box.maxX - box.minX, box.maxY - box.minY, 1e-6) * 0.05;
                sites[i] = {
                    x: refuge.x + (jitter() - 0.5) * spread,
                    y: refuge.y + (jitter() - 0.5) * spread,
                };
                weights[i] = weights[largest];
            }
        }
    }
    if (best && best.error < worstError) {
        rings2 = best.rings;
        for (let i = 0; i < sites.length; i += 1)
            sites[i] = best.sites[i];
        for (let i = 0; i < weights.length; i += 1)
            weights[i] = best.weights[i];
        worstError = best.error;
    }
    const cells = targets.map((target, i) => {
        const ring2 = rings2[i] ?? [];
        const ring = ring2.length >= 3 ? projection.unprojectRing(ring2) : [];
        return {
            id: target.id,
            ring,
            area: ring.length >= 3 ? polygonArea(ring) : 0,
            site: projection.unproject(sites[i]),
            weight: weights[i],
        };
    });
    return { cells, iterations, worstError, converged: worstError <= tolerance };
}
/** Convenience for callers holding lat/lon rings. */
export function partitionLatLon(parentRing, targets, options) {
    return partition(parentRing.map(toVec3), targets, options);
}
export { hashSeed };
//# sourceMappingURL=partition.js.map